import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserManagementUser, AppRole } from '../../types/organization.types';
import { useUpdateUserRole, useToggleUserStatus } from '../../hooks/useUserManagement';
import { format } from 'date-fns';
import { Search, MoreVertical, Edit, UserX, UserCheck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const ROLES: { value: AppRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'talent_manager', label: 'Talent Manager' },
  { value: 'content_strategist', label: 'Content Strategist' },
  { value: 'account_executive', label: 'Account Executive' },
  { value: 'finance', label: 'Finance' },
  { value: 'account_manager', label: 'Account Manager' },
];

export interface UserManagementTableProps {
  users: UserManagementUser[];
  isLoading: boolean;
}

export const UserManagementTable: React.FC<UserManagementTableProps> = ({ users, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<UserManagementUser | null>(null);
  const [newRole, setNewRole] = useState<AppRole>('account_manager');
  const [confirmAction, setConfirmAction] = useState<{
    user: UserManagementUser;
    action: 'suspend' | 'activate';
  } | null>(null);

  const updateUserRole = useUpdateUserRole();
  const toggleUserStatus = useToggleUserStatus();

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.organization_role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleRoleUpdate = () => {
    if (editingUser && newRole !== editingUser.organization_role) {
      updateUserRole.mutate(
        { userId: editingUser.id, newRole },
        {
          onSuccess: () => {
            setEditingUser(null);
          },
          onError: (error) => {
            console.error('Role update failed:', error);
          }
        }
      );
    } else {
      setEditingUser(null);
    }
  };

  const handleStatusToggle = () => {
    if (confirmAction) {
      const newStatus = confirmAction.action === 'suspend' ? 'suspended' : 'active';
      toggleUserStatus.mutate(
        {
          userId: confirmAction.user.id,
          newStatus: newStatus as 'active' | 'suspended'
        },
        {
          onSuccess: () => {
            setConfirmAction(null);
          },
          onError: (error) => {
            console.error('Status toggle failed:', error);
          }
        }
      );
    }
  };

  const getRoleLabel = (role: AppRole) => {
    return ROLES.find(r => r.value === role)?.label || role;
  };

  const handleEditRole = (user: UserManagementUser) => {
    setEditingUser(user);
    setNewRole(user.organization_role);
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map(role => (
              <SelectItem key={role.value} value={role.value}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {getRoleLabel(user.organization_role)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={user.status === 'active' ? 'default' : 'destructive'}
                    className={user.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-200' : ''}
                  >
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(new Date(user.created_at), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell>
                  {user.last_login
                    ? format(new Date(user.last_login), 'MMM dd, yyyy')
                    : 'Never'
                  }
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditRole(user)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Change Role
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setConfirmAction({
                          user,
                          action: user.status === 'active' ? 'suspend' : 'activate'
                        })}
                      >
                        {user.status === 'active' ? (
                          <>
                            <UserX className="w-4 h-4 mr-2" />
                            Suspend User
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Activate User
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No users found matching your criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {editingUser?.name} ({editingUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRole} onValueChange={(value: AppRole) => setNewRole(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select new role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRoleUpdate}
              disabled={updateUserRole.isPending || newRole === editingUser?.organization_role}
            >
              {updateUserRole.isPending ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Status Change Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.action === 'suspend' ? 'Suspend User' : 'Activate User'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {confirmAction?.action} {confirmAction?.user.name}?
              {confirmAction?.action === 'suspend' && ' They will no longer be able to access the system.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmAction?.action === 'suspend' ? 'destructive' : 'default'}
              onClick={handleStatusToggle}
              disabled={toggleUserStatus.isPending}
            >
              {toggleUserStatus.isPending ? 'Processing...' :
                confirmAction?.action === 'suspend' ? 'Suspend User' : 'Activate User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementTable;
